import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useScrollReveal } from "../hooks/useScrollReveal";
import {
  TrendingUp,
  BadgePercent,
  Users,
  Truck,
  HeartHandshake,
  Headphones,
  Phone,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  {
    icon: TrendingUp,
    title: "Exclusive Territory",
    desc: "Secure your distribution rights in your city or region",
  },
  {
    icon: BadgePercent,
    title: "Direct Factory Price",
    desc: "No middlemen — maximum margins for your business",
  },
  {
    icon: Users,
    title: "Marketing Support",
    desc: "Branding materials, social media assets & more",
  },
  {
    icon: Truck,
    title: "Priority Fulfillment",
    desc: "Your orders always get first-in-queue processing",
  },
  {
    icon: HeartHandshake,
    title: "Flexible MOQ",
    desc: "Start from just 50kg and scale at your own pace",
  },
  {
    icon: Headphones,
    title: "Dedicated Support",
    desc: "Personal account manager for seamless operations",
  },
];

const volumeOptions = [
  "Less than 100 kg/month",
  "100 – 500 kg/month",
  "500 kg – 1 Ton/month",
  "1 – 5 Tons/month",
  "5+ Tons/month",
];

const initialForm = {
  company_name: "",
  contact_person: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  gst_number: "",
  volume_requirement: "",
  message: "",
};

const DistributorSection = () => {
  const { ref, isVisible } = useScrollReveal();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(initialForm);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_person || !form.phone || !form.volume_requirement) {
      toast.error("Please fill in all required fields.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${BACKEND_URL}/api/distributor-enquiry`, form);
      toast.success(res.data.message);
      setForm(initialForm);
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="distributor"
      ref={ref}
      className="py-24 bg-[#0A0A0A] relative overflow-hidden"
      data-testid="distributor-section"
    >
      {/* Background gold glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/3 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Section Header */}
        <div
          className="text-center mb-14"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <span className="text-[#D4AF37] text-xs uppercase tracking-[5px] mb-4 block font-medium">
            B2B Partnership
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#FAFAFA] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="distributor-heading"
          >
            Partner with MakhanaG
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Join our growing network of distributors and retailers across India.
            Be part of the premium makhana revolution.
          </p>
          <p
            className="text-[#D4AF37]/80 mt-2"
            style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}
          >
            डिस्ट्रीब्यूटर बनें — सीधे फ़ैक्टरी रेट पर मखाना पाएं
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Benefits */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(-30px)",
              transition: "opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s",
            }}
          >
            <h3
              className="text-[#FAFAFA] text-2xl font-semibold mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Why Distribute MakhanaG?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={b.title}
                    className="flex items-start gap-3 p-4 rounded-xl bg-[#121212] border border-white/5 hover:border-[#D4AF37]/20 transition-all duration-300 group"
                    data-testid={`distributor-benefit-${i}`}
                  >
                    <div className="p-2 rounded-lg bg-[#D4AF37]/10 group-hover:bg-[#D4AF37]/20 transition-colors shrink-0">
                      <Icon className="text-[#D4AF37]" size={16} />
                    </div>
                    <div>
                      <p className="text-[#FAFAFA] text-sm font-semibold mb-1">
                        {b.title}
                      </p>
                      <p className="text-[#A1A1AA] text-xs leading-relaxed">
                        {b.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick Contact Card */}
            <div className="p-6 bg-[#121212] border border-[#D4AF37]/20 rounded-2xl">
              <p className="text-[#D4AF37] font-semibold mb-4 flex items-center gap-2">
                <Phone size={14} />
                Quick Contact
              </p>
              <div className="space-y-3">
                <a
                  href="tel:+919502343899"
                  className="flex items-center gap-3 text-sm group"
                  data-testid="distributor-call-link"
                >
                  <span className="text-[#525252] text-xs uppercase tracking-widest w-16">Call</span>
                  <span className="text-[#FAFAFA] group-hover:text-[#D4AF37] transition-colors font-medium">
                    +91-9502343899
                  </span>
                </a>
                <a
                  href="https://wa.me/919502343899?text=Hi%2C%20I%27m%20interested%20in%20becoming%20a%20MakhanaG%20distributor"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 text-sm group"
                  data-testid="distributor-whatsapp-link"
                >
                  <span className="text-[#525252] text-xs uppercase tracking-widest w-16">WhatsApp</span>
                  <span className="text-[#FAFAFA] group-hover:text-green-400 transition-colors font-medium">
                    +91-9502343899
                  </span>
                </a>
                <a
                  href="mailto:info@nexfoxnourish.in"
                  className="flex items-center gap-3 text-sm group"
                  data-testid="distributor-email-link"
                >
                  <span className="text-[#525252] text-xs uppercase tracking-widest w-16">Email</span>
                  <span className="text-[#FAFAFA] group-hover:text-[#D4AF37] transition-colors font-medium break-all">
                    info@nexfoxnourish.in
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Right: Enquiry Form */}
          <div
            className="bg-[#121212] border border-white/5 hover:border-[#D4AF37]/15 rounded-2xl p-6 md:p-8 transition-colors duration-500"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(30px)",
              transition: "opacity 0.7s ease 0.35s, transform 0.7s ease 0.35s",
            }}
            data-testid="distributor-form-card"
          >
            <h3
              className="text-[#FAFAFA] text-2xl font-semibold mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Distributor Enquiry
            </h3>
            <p className="text-[#525252] text-sm mb-6">
              Fill the form and our team will reach out within 24 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="distributor-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                    Company Name *
                  </label>
                  <input
                    name="company_name"
                    value={form.company_name}
                    onChange={handleChange}
                    placeholder="Your Company"
                    required
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                    data-testid="distributor-company-name"
                  />
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                    Contact Person *
                  </label>
                  <input
                    name="contact_person"
                    value={form.contact_person}
                    onChange={handleChange}
                    placeholder="Full Name"
                    required
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                    data-testid="distributor-contact-person"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                    Phone Number *
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    required
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                    data-testid="distributor-phone"
                  />
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                    Email Address
                  </label>
                  <input
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    type="email"
                    placeholder="you@company.com"
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                    data-testid="distributor-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                    City
                  </label>
                  <input
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="Your City"
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                    data-testid="distributor-city"
                  />
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                    State
                  </label>
                  <input
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    placeholder="Your State"
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                    data-testid="distributor-state"
                  />
                </div>
              </div>

              <div>
                <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                  Monthly Volume Requirement *
                </label>
                <select
                  name="volume_requirement"
                  value={form.volume_requirement}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm outline-none transition-colors cursor-pointer"
                  data-testid="distributor-volume"
                >
                  <option value="" className="bg-[#0A0A0A]">
                    Select monthly volume
                  </option>
                  {volumeOptions.map((opt) => (
                    <option key={opt} value={opt} className="bg-[#0A0A0A]">
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                  GST Number{" "}
                  <span className="text-[#525252] normal-case tracking-normal">
                    (Optional)
                  </span>
                </label>
                <input
                  name="gst_number"
                  value={form.gst_number}
                  onChange={handleChange}
                  placeholder="GST Registration Number"
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                  data-testid="distributor-gst"
                />
              </div>

              <div>
                <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                  Message{" "}
                  <span className="text-[#525252] normal-case tracking-normal">
                    (Optional)
                  </span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tell us about your requirements, target market, or any specific needs..."
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors resize-none"
                  data-testid="distributor-message"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black py-4 rounded-full font-bold text-base hover:bg-[#F4E3B2] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_40px_rgba(212,175,55,0.5)] disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02]"
                data-testid="distributor-submit-btn"
              >
                {loading ? "Submitting Enquiry..." : "Submit Distributor Enquiry"}
              </button>

              <p className="text-[#525252] text-xs text-center">
                By submitting, you agree to be contacted by our team. We never share your information.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DistributorSection;
